// Acción Tardía — manual catch-up form per SPEC §11.3.
// Same intake as NewCase but adds fecha_real_notificacion (required) and a reason.
// Cases land with late_entry=true, late_entry_approved=false, hidden from abogados
// until an admin flips the gate from the case detail General tab.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cases as casesApi, personas as personasApi, users as usersApi } from "../api/endpoints";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import { TIPO_LABEL } from "../components/Pills";
import { useToast } from "../components/Toast";
import { useDebounced } from "../components/useDebounced";
import LitisoftFields from "../components/LitisoftFields";
import { TERMINO_OPTIONS, formatTermino } from "../utils/termino";

const TIPOS = ["tutela", "fallo", "requerimiento", "desacato", "sancion"];
const TIPO_FALLO = ["FAVORABLE_1A","DESFAVORABLE_1A","FAVORABLE_2A","DESFAVORABLE_2A","NO_PEDIR_INSUMO"];

export default function LateEntry() {
  const nav = useNavigate();
  const toast = useToast();
  const [tipo, setTipo] = useState("tutela");
  const [busy, setBusy] = useState(false);

  // Persona
  const [cedula, setCedula] = useState("");
  const [nombre, setNombre] = useState("");
  const [persona_id, setPersonaId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const dq = useDebounced(cedula, 250);
  useEffect(() => {
    if (!dq || persona_id) return;
    personasApi.search(dq).then(r => setSuggestions(r.data)).catch(() => setSuggestions([]));
  }, [dq, persona_id]);

  // Base
  const [radicado, setRadicado] = useState("");
  const [juzgado, setJuzgado] = useState("");
  const [terminoHoras, setTerminoHoras] = useState("");
  const [abogadoId, setAbogadoId] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [tipoFallo, setTipoFallo] = useState("");

  // Late-entry specific
  const [fechaRealNotif, setFechaRealNotif] = useState("");
  const [reason, setReason] = useState("");

  // feature/litisoft-export LITISOFT optional fields
  const [litisoft, setLitisoft] = useState({});

  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => { usersApi.list().then(r => setAllUsers(r.data)); }, []);

  const radicadoDeb = useDebounced(radicado, 400);
  const [dup, setDup] = useState({ strict: [], warning: [] });
  // Sprint 63 — pass tipo so the backend qualifies strict matches.
  // Same radicado + DIFFERENT tipo is a legitimate cross-actuación and
  // shouldn't block.
  useEffect(() => {
    if (!radicadoDeb) { setDup({ strict: [], warning: [] }); return; }
    casesApi.checkDuplicates(radicadoDeb, nombre, tipo)
      .then(r => setDup(r.data)).catch(() => {});
  }, [radicadoDeb, nombre, tipo]);
  const [strictModal, setStrictModal] = useState(false);
  useEffect(() => { if (dup.strict?.length) setStrictModal(true); }, [dup.strict?.length]);

  // feature/single-role-audit — FALSE POSITIVE: list filter on PRIMARY
  // role for the assignment dropdown.
  const abogados = useMemo(() => allUsers.filter(u => u.role === "abogado" && u.active), [allUsers]);

  async function submit(e) {
    e.preventDefault();
    if (dup.strict?.length) { setStrictModal(true); return; }
    if (!fechaRealNotif) { toast.push("La fecha real de notificación es obligatoria", "error"); return; }
    if (!reason.trim())  { toast.push("Indique el motivo del registro extemporáneo", "error"); return; }
    // feature/intake-swap-guard — C1.5. Same digit-presence guard as
    // NewCase. Late-entry intake is the other code path that creates
    // cases via POST /api/cases, so the same swap risk applies.
    if (!/\d/.test(radicado.trim())) {
      toast.push("El radicado debe contener números. Verifique que no haya escrito el nombre del accionante en este campo.", "error");
      return;
    }

    const payload = {
      tipo, radicado: radicado.trim(), juzgado: juzgado.trim() || null,
      observaciones: reason ? `${reason}${observaciones ? "\n\n" + observaciones : ""}` : observaciones,
      late_entry: true,
      fecha_real_notificacion: fechaRealNotif,
      ...litisoft,
    };
    if (persona_id) payload.persona_id = persona_id;
    else payload.persona = { cedula: cedula || null, nombre: nombre.trim() };

    if (tipo === "tutela") { payload.termino_horas = Number(terminoHoras); payload.abogado_asignado_id = Number(abogadoId); }
    if (tipo === "fallo") payload.tipo_fallo = tipoFallo;
    if (tipo !== "fallo" && abogadoId) payload.abogado_asignado_id = Number(abogadoId);

    setBusy(true);
    try {
      const r = await casesApi.create(payload);
      toast.push(`Caso ${r.data.case_number} registrado como extemporáneo. Pendiente de aprobación.`, "success");
      nav(`/cases?open=${r.data.id}`);
    } catch (err) {
      const e = err?.response?.data;
      if (err?.response?.status === 409 && e?.existing) {
        setDup(prev => ({ ...prev, strict: [e.existing] }));
        setStrictModal(true);
      } else {
        // Sprint 63+ backends ship `message` (human) alongside `error` (code).
        toast.push(e?.message || e?.error || "Error al crear", "error");
      }
    } finally { setBusy(false); }
  }

  const todayLabel = new Date().toLocaleDateString("es-CO", { year: "numeric", month: "long", day: "numeric" });

  return (
    <form className="col" style={{ gap: 16 }} onSubmit={submit}>
      <h1 style={{ margin: 0 }}>Registro extemporáneo</h1>

      <div className="warn-inline">
        ⚠ La fecha de ingreso será <strong>hoy ({todayLabel})</strong>. El caso queda marcado como <strong>registro extemporáneo</strong> y requiere aprobación de un administrador antes de ser visible para los abogados.
      </div>

      <div className="card">
        <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
          {TIPOS.map(t => (
            <label key={t} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
              <input type="radio" checked={tipo === t} onChange={() => setTipo(t)} /> {TIPO_LABEL[t]}
            </label>
          ))}
        </div>
        <div className="rule" style={{ width: 32 }} />

        <div className="grid-2">
          <div style={{ position: "relative" }}>
            <label>Cédula del accionante</label>
            {/* feature/intake-swap-guard — C1.5. autoComplete="off" on
                all four intake fields to defeat browser / password-
                manager autofill heuristics. */}
            <input value={cedula} onChange={(e) => { setPersonaId(null); setCedula(e.target.value); }} autoComplete="off" name="cedula" />
            {suggestions.length > 0 && !persona_id && (
              <div style={{ position: "absolute", left: 0, right: 0, top: 64, background: "var(--bg-card)", border: "1px solid var(--border-hairline)", borderRadius: 8, boxShadow: "var(--shadow-md)", zIndex: 5 }}>
                {suggestions.map(s => (
                  <div key={s.id} style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-hairline)" }}
                       onClick={() => { setPersonaId(s.id); setCedula(s.cedula || ""); setNombre(s.nombre); setSuggestions([]); }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{s.nombre}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>CC {s.cedula || "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label>Nombre del accionante</label>
            <input value={nombre} onChange={(e) => { setPersonaId(null); setNombre(e.target.value); }} required autoComplete="off" name="accionante_nombre" />
          </div>

          <div>
            <label>Radicado</label>
            <input value={radicado} onChange={(e) => setRadicado(e.target.value)} required autoComplete="off" name="radicado" inputMode="text" />
            {!!dup.strict?.length && (
              <div className="warn-inline" style={{ marginTop: 6, background: "var(--color-danger-bg)", borderColor: "var(--color-danger)", color: "var(--color-danger)" }}>
                ⛔ Ya existe un caso con este radicado y la misma actuación ({TIPO_LABEL[dup.strict[0]?.tipo] || dup.strict[0]?.tipo} · {dup.strict[0]?.case_number}).
              </div>
            )}
            {/* Sprint 63 — informational chip when same radicado has a
                DIFFERENT actuación. Legitimate cross-actuación (Tutela →
                Fallo on the same proceso); doesn't block submit. */}
            {!dup.strict?.length && (dup.warning || []).filter((w) => w.reason === "cross_actuacion").map((w) => (
              <div key={`cross-${w.id}`} className="warn-inline" style={{
                marginTop: 6,
                background: "var(--color-info-bg)",
                borderColor: "var(--accent-mint)",
                color: "var(--accent-mint)",
              }}>
                ℹ️ Este radicado ya tiene {TIPO_LABEL[w.tipo] || w.tipo} ({w.case_number}). Puede registrar la nueva actuación; quedan vinculadas al mismo proceso.
              </div>
            ))}
          </div>
          <div>
            <label>Juzgado</label>
            <input value={juzgado} onChange={(e) => setJuzgado(e.target.value)} required={tipo === "tutela"} autoComplete="off" name="juzgado" />
          </div>

          {tipo === "tutela" && (
            <>
              <div>
                <label>Término</label>
                <select value={terminoHoras} onChange={(e) => setTerminoHoras(e.target.value)} required>
                  <option value="">— elegir —</option>
                  {TERMINO_OPTIONS.map(h => <option key={h} value={h}>{formatTermino(h)}</option>)}
                </select>
              </div>
              <div>
                <label>Abogado asignado</label>
                <select value={abogadoId} onChange={(e) => setAbogadoId(e.target.value)} required>
                  <option value="">— elegir —</option>
                  {abogados.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </>
          )}

          {tipo === "fallo" && (
            <div style={{ gridColumn: "span 2" }}>
              <label>Tipo de fallo</label>
              <select value={tipoFallo} onChange={(e) => setTipoFallo(e.target.value)} required>
                <option value="">— elegir —</option>
                {TIPO_FALLO.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          )}

          {(tipo === "requerimiento" || tipo === "desacato" || tipo === "sancion") && (
            <div>
              <label>Abogado (opcional)</label>
              <select value={abogadoId} onChange={(e) => setAbogadoId(e.target.value)}>
                <option value="">— sin asignar —</option>
                {abogados.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Datos del registro extemporáneo</h2>
        <div className="rule" />
        <div className="grid-2">
          <div>
            <label>Fecha real de notificación</label>
            <input type="date" value={fechaRealNotif} onChange={(e) => setFechaRealNotif(e.target.value)} required max={new Date().toISOString().slice(0,10)} />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>La fecha en que el juzgado notificó realmente. La fecha de ingreso al sistema permanece como hoy.</div>
          </div>
          <div>
            <label>Motivo del registro extemporáneo</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="Por qué este caso no se registró el día que llegó" />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label>Observaciones adicionales</label>
            <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <LitisoftFields values={litisoft} onChange={setLitisoft} />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="submit" disabled={busy || !!dup.strict?.length}>
          {busy && <Spinner color="currentColor" />} Guardar registro extemporáneo
        </button>
      </div>

      <Modal
        open={strictModal}
        title="Radicado duplicado"
        onClose={() => setStrictModal(false)}
        footer={<button className="secondary" onClick={() => setStrictModal(false)}>Cerrar</button>}
      >
        <p>Ya existe un caso con este radicado y la misma actuación:</p>
        {dup.strict.map(s => (
          <div key={s.id} className="card tight" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 600 }}>{s.case_number}</div>
            <div className="muted" style={{ fontSize: 12 }}>{s.accionante_nombre}</div>
          </div>
        ))}
      </Modal>
    </form>
  );
}
